<template>
  <div class="grid grid-cols-4 gap-2 pt-2 border-t border-gray-700">

    <div class="filter-group">
      <label class="filter-label">Status</label>
      <multiselect
        v-model="selectedStatus"
        :options="statusOptions"
        track-by="value"
        label="label"
        placeholder="Todos"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :searchable="false"
        @update:model-value="apply"
      >
        <template #noOptions>Sem opções</template>
      </multiselect>
    </div>

    <div class="filter-group">
      <label class="filter-label">Família</label>
      <multiselect
        v-model="selectedFamilia"
        :options="familiaOptions"
        track-by="value"
        label="label"
        placeholder="Todas"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :searchable="true"
        @update:model-value="onFamiliaChange"
      >
        <template #noResult>Nenhuma encontrada</template>
        <template #noOptions>Sem opções</template>
      </multiselect>
    </div>

    <div class="filter-group">
      <label class="filter-label">Produto</label>
      <multiselect
        v-model="selectedProduto"
        :options="filteredProdutoOptions"
        track-by="value"
        label="label"
        placeholder="Todos"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :searchable="true"
        @update:model-value="onProdutoChange"
      >
        <template #noResult>Nenhum encontrado</template>
        <template #noOptions>Sem opções</template>
      </multiselect>
    </div>

    <div class="filter-group">
      <label class="filter-label">Tipologia</label>
      <multiselect
        v-model="selectedTipologia"
        :options="filteredTipologiaOptions"
        track-by="value"
        label="label"
        placeholder="Todas"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :searchable="true"
        @update:model-value="apply"
      >
        <template #noResult>Nenhuma encontrada</template>
        <template #noOptions>Sem opções</template>
      </multiselect>
    </div>

    <div class="filter-group">
      <label class="filter-label">Marca</label>
      <multiselect
        v-model="selectedMarca"
        :options="marcaOptions"
        track-by="value"
        label="label"
        placeholder="Todas"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :searchable="true"
        @update:model-value="apply"
      >
        <template #noResult>Nenhuma encontrada</template>
        <template #noOptions>Sem opções</template>
      </multiselect>
    </div>

    <div class="filter-group">
      <label class="filter-label">Medida</label>
      <multiselect
        v-model="selectedMedida"
        :options="medidaOptions"
        track-by="value"
        label="label"
        placeholder="Todas"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :searchable="true"
        @update:model-value="apply"
      >
        <template #noResult>Nenhuma encontrada</template>
        <template #noOptions>Sem opções</template>
      </multiselect>
    </div>

    <div class="filter-group">
      <label class="filter-label">Serviço</label>
      <multiselect
        v-model="selectedServico"
        :options="servicoOptions"
        track-by="value"
        label="label"
        placeholder="Todos"
        :multiple="true"
        :close-on-select="false"
        :clear-on-select="false"
        :searchable="true"
        @update:model-value="apply"
      >
        <template #noResult>Nenhum encontrado</template>
        <template #noOptions>Sem opções</template>
      </multiselect>
    </div>

    <div class="flex items-end">
      <button
        class="w-full text-xs text-gray-400 hover:text-[var(--text)] border border-gray-600 hover:border-gray-400 rounded px-2 py-1.5 transition-colors"
        @click="clear"
      >
        Limpar filtros
      </button>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, defineExpose } from 'vue'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'

const props = defineProps({
  statusOptions:    { type: Array, default: () => [] },
  familiaOptions:   { type: Array, default: () => [] },
  produtoOptions:   { type: Array, default: () => [] },
  tipologiaOptions: { type: Array, default: () => [] },
  marcaOptions:     { type: Array, default: () => [] },
  medidaOptions:    { type: Array, default: () => [] },
  servicoOptions:   { type: Array, default: () => [] },
})

const emit = defineEmits(['filtersChanged'])

const selectedStatus    = ref([])
const selectedFamilia   = ref([])
const selectedProduto   = ref([])
const selectedTipologia = ref([])
const selectedMarca     = ref([])
const selectedMedida    = ref([])
const selectedServico   = ref([])

const filteredProdutoOptions = computed(() => {
  if (!selectedFamilia.value.length) return props.produtoOptions
  const labels = selectedFamilia.value.map(f => f.label)
  return props.produtoOptions.filter(o => labels.some(l => o.label.includes(`(${l})`)))
})

const filteredTipologiaOptions = computed(() => {
  let opts = props.tipologiaOptions
  if (selectedFamilia.value.length) {
    const labels = selectedFamilia.value.map(f => f.label)
    opts = opts.filter(o => labels.some(l => o.label.includes(l)))
  }
  if (selectedProduto.value.length) {
    const names = selectedProduto.value.map(p => p.label.split(' (')[0])
    opts = opts.filter(o => names.some(n => o.label.includes(n)))
  }
  return opts
})

const activeFilterCount = computed(() =>
  [selectedStatus, selectedFamilia, selectedProduto, selectedTipologia, selectedMarca, selectedMedida, selectedServico]
    .filter(s => s.value.length > 0).length
)

const apply = () => {
  const filters = {}
  const push = (arr, key) => { if (arr.length) filters[key] = arr.map(o => o.value).join(',') }
  push(selectedStatus.value,    'status')
  push(selectedFamilia.value,   'familia_id')
  push(selectedProduto.value,   'produto_id')
  push(selectedTipologia.value, 'tipologia_id')
  push(selectedMarca.value,     'tpMarca_id')
  push(selectedMedida.value,    'tpMedida_id')
  push(selectedServico.value,   'tpServico_id')
  emit('filtersChanged', filters)
}

const onFamiliaChange = () => {
  selectedProduto.value   = selectedProduto.value.filter(p => filteredProdutoOptions.value.some(o => o.value === p.value))
  selectedTipologia.value = selectedTipologia.value.filter(t => filteredTipologiaOptions.value.some(o => o.value === t.value))
  apply()
}

const onProdutoChange = () => {
  selectedTipologia.value = selectedTipologia.value.filter(t => filteredTipologiaOptions.value.some(o => o.value === t.value))
  apply()
}

const clear = () => {
  selectedStatus.value    = []
  selectedFamilia.value   = []
  selectedProduto.value   = []
  selectedTipologia.value = []
  selectedMarca.value     = []
  selectedMedida.value    = []
  selectedServico.value   = []
  emit('filtersChanged', {})
}

const setFilters = (equipamento) => {
  const findOpt = (options, id) => {
    if (id === undefined || id === null) return null
    return options.find(o => o.value === id) ?? null
  }
  selectedStatus.value    = []
  selectedFamilia.value   = [findOpt(props.familiaOptions,   equipamento.familia?.id)].filter(Boolean)
  selectedProduto.value   = [findOpt(props.produtoOptions,   equipamento.produto?.id)].filter(Boolean)
  selectedTipologia.value = [findOpt(props.tipologiaOptions, equipamento.tipologia?.id)].filter(Boolean)
  // selectedMarca.value     = [findOpt(props.marcaOptions,     equipamento.marca?.id)].filter(Boolean)
  selectedMedida.value    = [findOpt(props.medidaOptions,    equipamento.medida?.id)].filter(Boolean)
  selectedServico.value   = equipamento.servico?.id !== undefined && equipamento.servico.id !== 0
    ? [findOpt(props.servicoOptions, equipamento.servico.id)].filter(Boolean)
    : []
  apply()
}

defineExpose({ clear, activeFilterCount, setFilters })
</script>

<style scoped>
.filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-label {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
}

:deep(.multiselect) {
  background: var(--fundoApp);
  border: 1px solid #4b5563;
  border-radius: 4px;
  color: #fff;
  min-height: 30px;
  font-size: 11px;
}

:deep(.multiselect__tags) {
  background: var(--fundoApp);
  border: none;
  min-height: 30px;
  padding: 3px 36px 3px 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

:deep(.multiselect__input),
:deep(.multiselect__single) {
  background: transparent;
  color: #fff;
  font-size: 11px;
  margin: 0;
  padding: 0;
}

:deep(.multiselect__placeholder) {
  color: #6b7280;
  font-size: 11px;
  margin-bottom: 6px;
  padding-top: 0;
}

:deep(.multiselect__select) {
  height: 30px;
}

:deep(.multiselect__content-wrapper) {
  background: var(--fundoForm);
  border: 1px solid #4b5563;
  font-size: 11px;
}

:deep(.multiselect__option) {
  color: #d1d5db;
  padding: 8px 12px;
  min-height: 32px;
}

:deep(.multiselect__option--highlight) {
  background: #2563eb;
  color: #fff;
}

:deep(.multiselect__option--selected) {
  background: #1d4ed8;
  color: #fff;
  font-weight: 500;
}

:deep(.multiselect__option--selected.multiselect__option--highlight) {
  background: #2563eb;
  color: #fff;
}

:deep(.multiselect__tag) {
  background: rgba(59, 130, 246, 0.2);
  border: 1px solid rgba(59, 130, 246, 0.4);
  color: #93c5fd;
  border-radius: 4px;
  padding: 1px 20px 1px 6px;
  font-size: 10px;
  margin: 0;
}

:deep(.multiselect__tag-icon::after) { color: #93c5fd; }
:deep(.multiselect__tag-icon:hover) { background: rgba(239, 68, 68, 0.3); }
:deep(.multiselect__tag-icon:hover::after) { color: #fca5a5; }
</style>
